import { Link, useLocation } from "react-router-dom";
import { Scale, Scroll, Calculator } from "lucide-react";

/**
 * Top-level calculator switcher. A single thin band rendered immediately
 * below the SiteHeader + CampaignBanner on every calculator page so the
 * active calculator owns the page below it and the alternatives never
 * compete for attention inside the main content column.
 *
 *   [ Maintenance ]   [ Estate ]   [ Transfer ← active ]
 *
 * The `active` is auto-derived from the current pathname; an explicit
 * prop overrides if provided.
 */
const TABS = [
  { key: "maintenance",     to: "/calculators/maintenance",     label: "Maintenance", Icon: Scale },
  { key: "estate",          to: "/calculators/deceased-estate", label: "Estate",      Icon: Scroll },
  { key: "transfer",        to: "/calculators",                 label: "Transfer",    Icon: Calculator },
];

function deriveActive(pathname) {
  if (pathname.startsWith("/calculators/maintenance"))     return "maintenance";
  if (pathname.startsWith("/calculators/deceased-estate")) return "estate";
  return "transfer";
}

export default function CalculatorSwitcher({ active }) {
  const location = useLocation();
  const current  = active || deriveActive(location.pathname);

  return (
    <div className="bg-white border-b border-zinc-200" data-testid="calculator-switcher">
      <div className="mx-auto max-w-7xl px-6 lg:px-10 py-3 flex items-center gap-3 lg:gap-5 flex-wrap">
        <div className="text-[10px] tracking-[0.22em] uppercase text-zinc-400 mr-1 shrink-0">
          Other calculators:
        </div>
        <nav className="flex flex-wrap items-center gap-2" data-testid="calculator-switcher-tabs">
          {TABS.map(({ key, to, label, Icon }) => {
            const isActive = key === current;
            return (
              <Link
                key={key}
                to={to}
                aria-current={isActive ? "page" : undefined}
                data-testid={`switcher-tab-${key}${isActive ? "-active" : ""}`}
                className={
                  isActive
                    ? "inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium tracking-wide bg-zinc-900 text-white"
                    : "inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50 transition-colors border border-transparent hover:border-zinc-200"
                }
              >
                <Icon className="h-3 w-3" strokeWidth={1.75} />
                {label}
              </Link>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
